import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useAssetLibrary,
  useChatAssets,
  useImportAssetForChat,
  type LibraryAsset,
} from '../hooks/useApi';

interface LibraryModalProps {
  chatId: string;
  instanceId: string;
  onClose: () => void;
}

/**
 * Modal "📚 Importar de biblioteca" — busca refs cross-proyecto/cliente
 * para reusarlas en el asset actual sin re-uploadear el archivo. El backend
 * clona la fila DB compartiendo bucketKey.
 */
export function LibraryModal({ chatId, instanceId, onClose }: LibraryModalProps) {
  const [q, setQ] = useState('');
  const [client, setClient] = useState<string | undefined>(undefined);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const { data: items, isLoading } = useAssetLibrary({ q, client, projectId });
  const { data: existing } = useChatAssets(chatId);
  const importAsset = useImportAssetForChat(chatId, instanceId);
  const [importingId, setImportingId] = useState<string | null>(null);

  // Para marcar las que ya están en este chat (mismo bucketKey).
  const existingBucketKeys = useMemo(
    () => new Set((existing ?? []).map((a) => a.bucketUrl).filter(Boolean)),
    [existing],
  );

  // Sidebar de filtros: agrupar por cliente → proyectos.
  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, { id: string; name: string; count: number }>>();
    for (const a of items ?? []) {
      const c = a.projectClient ?? '(sin cliente)';
      if (!map.has(c)) map.set(c, new Map());
      const projects = map.get(c)!;
      const existing = projects.get(a.projectSlug) ?? { id: a.projectId, name: a.projectName, count: 0 };
      existing.count++;
      projects.set(a.projectSlug, existing);
    }
    return map;
  }, [items]);

  const handleImport = async (a: LibraryAsset) => {
    setImportingId(a.id);
    try {
      await importAsset.mutateAsync(a.id);
    } finally {
      setImportingId(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-6xl h-[85vh] bg-slate-900 rounded-xl border border-slate-700 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div>
            <div className="text-sm font-medium text-slate-100">📚 Importar de biblioteca</div>
            <div className="text-xs text-slate-500">
              Refs de todos tus proyectos/clientes. Click "+ Importar" para sumarla a este asset.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-4 py-2 border-b border-slate-800">
          <input
            type="text"
            placeholder="Buscar por descripción, nombre…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision"
          />
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar filtros */}
          <div className="w-56 border-r border-slate-800 overflow-y-auto p-2">
            <button
              onClick={() => {
                setClient(undefined);
                setProjectId(undefined);
              }}
              className={`block w-full text-left text-xs px-2 py-1 rounded mb-2 ${
                !client && !projectId ? 'bg-vision-dark/40 text-white' : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              Todas ({items?.length ?? 0})
            </button>
            {Array.from(grouped.entries()).map(([clientName, projects]) => {
              const clientCount = Array.from(projects.values()).reduce((acc, p) => acc + p.count, 0);
              return (
                <div key={clientName} className="mb-2">
                  <button
                    onClick={() => {
                      setClient(clientName === '(sin cliente)' ? undefined : clientName);
                      setProjectId(undefined);
                    }}
                    className={`block w-full text-left text-xs font-medium px-2 py-1 rounded ${
                      client === clientName ? 'bg-slate-800 text-vision' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {clientName} <span className="text-slate-600">({clientCount})</span>
                  </button>
                  <div className="ml-3 mt-0.5 space-y-0.5">
                    {Array.from(projects).map(([slug, p]) => (
                      <button
                        key={slug}
                        onClick={() => setProjectId(p.id)}
                        className={`block w-full text-left text-[11px] px-2 py-1 rounded ${
                          projectId === p.id
                            ? 'bg-vision-dark/50 text-white'
                            : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
                        }`}
                      >
                        {p.name} <span className="text-slate-600">({p.count})</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-3">
            {isLoading && <div className="text-sm text-slate-500 p-4">Cargando…</div>}
            {items && items.length === 0 && (
              <div className="text-sm text-slate-500 italic p-8 text-center border border-dashed border-slate-700 rounded-lg">
                Sin resultados. Ajustá el filtro.
              </div>
            )}
            {items && items.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {items.map((a) => {
                  const alreadyImported = existingBucketKeys.has(a.bucketUrl);
                  const isImporting = importingId === a.id;
                  return (
                    <div
                      key={a.id}
                      className={`bg-slate-950 rounded-lg overflow-hidden border ${
                        alreadyImported ? 'border-emerald-700/60' : 'border-slate-700'
                      }`}
                    >
                      {a.mimeType.startsWith('image/') && (
                        <img
                          src={a.thumbnailUrl ?? a.accessUrl ?? a.falCdnUrl ?? ''}
                          alt=""
                          loading="lazy"
                          className="w-full h-28 object-cover"
                        />
                      )}
                      <div className="p-1.5 space-y-1">
                        <div className="text-[10px] text-slate-500 truncate" title={a.projectName}>
                          {a.projectClient ? `${a.projectClient} · ` : ''}
                          {a.projectName}
                        </div>
                        {a.metadata?.description && (
                          <div className="text-[11px] text-slate-200 line-clamp-2 leading-tight">
                            {a.metadata.description}
                          </div>
                        )}
                        <button
                          onClick={() => void handleImport(a)}
                          disabled={alreadyImported || isImporting}
                          className={`w-full text-[11px] py-1 rounded ${
                            alreadyImported
                              ? 'bg-emerald-900/40 text-emerald-300 cursor-default'
                              : 'bg-vision-dark hover:bg-vision text-white disabled:opacity-50'
                          }`}
                        >
                          {alreadyImported ? '✓ Ya en este asset' : isImporting ? '⏳ Importando…' : '+ Importar'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
